import { forwardRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Check, AlertCircle } from 'lucide-react'

type TacticalInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  error?: string
  success?: boolean
  hint?: string
  variant?: 'underline' | 'bordered' | 'floating'
  showCharCount?: boolean
  maxLength?: number
}

export const TacticalInput = forwardRef<HTMLInputElement, TacticalInputProps>(
  ({ 
    className, 
    label, 
    error, 
    success, 
    hint,
    variant = 'bordered',
    showCharCount = false,
    maxLength,
    id, 
    value,
    onFocus,
    onBlur,
    ...rest 
  }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const [isFocused, setIsFocused] = useState(false)
    const hasValue = Boolean(value)
    const charCount = typeof value === 'string' ? value.length : 0

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    // Floating label variant
    if (variant === 'floating') {
      return (
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            maxLength={maxLength}
            className={cn(
              'peer w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-4 pt-5 pb-2 text-sm text-foreground',
              'placeholder-transparent',
              'focus:outline-none focus:border-white/[0.15] focus:bg-white/[0.03] focus:shadow-[0_0_0_3px_rgba(255,255,255,0.03)]',
              'transition-all duration-200',
              error && 'border-destructive/40 focus:border-destructive/60',
              success && 'border-primary/40 focus:border-primary/60',
              className
            )}
            placeholder={label}
            {...rest}
          />
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                'absolute left-4 transition-all duration-200 pointer-events-none',
                'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-muted-foreground/40',
                'peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-muted-foreground/70',
                (hasValue || isFocused) ? 'top-2 translate-y-0 text-[10px] text-muted-foreground/70 uppercase tracking-wider' : ''
              )}
            >
              {label}
            </label>
          )}
          {/* Status icons */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {success && !error && <Check className="h-4 w-4 text-primary" />}
            {error && <AlertCircle className="h-4 w-4 text-destructive" />}
          </div>
          {/* Error/hint message */}
          {(error || hint) && (
            <p className={cn(
              'text-xs mt-1.5 ml-1',
              error ? 'text-destructive' : 'text-muted-foreground/50'
            )}>
              {error || hint}
            </p>
          )}
          {/* Character count */}
          {showCharCount && maxLength && (
            <p className={cn(
              'text-[10px] mt-1 text-right',
              charCount >= maxLength ? 'text-destructive' : 'text-muted-foreground/40'
            )}>
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      )
    }

    // Bordered variant (new default Stripe-style)
    if (variant === 'bordered') {
      return (
        <div className="space-y-1.5">
          {label && (
            <label
              htmlFor={inputId}
              className="block text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
            >
              {label}
            </label>
          )}
          <div className="relative">
            <input
              ref={ref}
              id={inputId}
              value={value}
              onFocus={handleFocus}
              onBlur={handleBlur}
              maxLength={maxLength}
              className={cn(
                'w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-foreground',
                'placeholder:text-muted-foreground/30',
                'focus:outline-none focus:border-white/[0.15] focus:bg-white/[0.03] focus:shadow-[0_0_0_3px_rgba(255,255,255,0.03)]',
                'transition-all duration-200',
                error && 'border-destructive/40 focus:border-destructive/60',
                success && 'border-primary/40 focus:border-primary/60',
                (success || error) && 'pr-10',
                className
              )}
              {...rest}
            />
            {/* Status icons */}
            {(success || error) && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {success && !error && <Check className="h-4 w-4 text-primary" />}
                {error && <AlertCircle className="h-4 w-4 text-destructive" />}
              </div>
            )}
          </div>
          {/* Error/hint message and char count */}
          <div className="flex items-center justify-between">
            {(error || hint) && (
              <p className={cn(
                'text-xs',
                error ? 'text-destructive' : 'text-muted-foreground/50'
              )}>
                {error || hint}
              </p>
            )}
            {showCharCount && maxLength && (
              <p className={cn(
                'text-[10px] ml-auto',
                charCount >= maxLength ? 'text-destructive' : 'text-muted-foreground/40'
              )}>
                {charCount}/{maxLength}
              </p>
            )}
          </div>
        </div>
      )
    }

    // Underline variant (legacy)
    return (
      <div className="space-y-1">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs text-muted-foreground/60"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            value={value}
            maxLength={maxLength}
            className={cn(
              'w-full bg-transparent border-b border-white/[0.1] py-2 text-sm text-foreground',
              'placeholder:text-muted-foreground/30',
              'focus:border-white/[0.25] focus:outline-none focus:ring-0',
              'transition-colors duration-200',
              error && 'border-destructive/50',
              success && 'border-primary/50',
              className
            )}
            {...rest}
          />
          {success && !error && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <Check className="h-3.5 w-3.5 text-primary" />
            </div>
          )}
        </div>
        {error && (
          <p className="text-xs text-destructive mt-1">{error}</p>
        )}
      </div>
    )
  }
)

TacticalInput.displayName = 'TacticalInput'
